import { FaroAiChip } from "@/components/common/ai-assets/ai-chip";
import {
  CircularProgress,
  CompassGoToIcon,
  cyan,
  FaroChipList,
  FaroChipStatus,
  FaroChipTag,
  FaroIconButton,
  FaroMenuSeparator,
  FaroTooltip,
  NO_TRANSLATE_CLASS,
  TruncatedFaroText,
} from "@faro-lotv/flat-ui";
import { Grid } from "@mui/material";
import { Stack } from "@mui/system";
import type { JSX } from "react";
import { CardAttachmentIcon } from "./card-attachment-icon";
import { CardContextMenu } from "./card-context-menu";
import { CardUserAvatar } from "./card-user-avatar";
import { AnnotationCardProps } from "./responsive-card-types";
import {
  RESPONSIVE_CARDS_HEIGHT,
  ResponsiveCardVariant,
} from "./responsive-card-variant";

/** @returns A card with a layout on one single line */
export function LargeCard({
  name,
  attachments,
  tags,
  state,
  mainAssignee,
  markup,
  aiMarkupData,
  dueDate,
  onGoTo,
  isLoading = false,
}: AnnotationCardProps): JSX.Element {
  return (
    <Stack
      sx={{ height: RESPONSIVE_CARDS_HEIGHT[ResponsiveCardVariant.large] }}
    >
      <Stack sx={{ p: 1.5 }}>
        <Grid container columnSpacing={1} sx={{ alignItems: "center" }}>
          <Grid size="auto">
            {isLoading && (
              <CircularProgress
                sx={{
                  color: cyan[500],
                }}
                size="14px"
              />
            )}
            <TruncatedFaroText
              className={NO_TRANSLATE_CLASS}
              variant="heading12"
              sx={{ width: "100px" }}
            >
              {name}
            </TruncatedFaroText>
          </Grid>
          <Grid
            size="auto"
            sx={{ minWidth: aiMarkupData?.aiDetectedTag ? "0px" : "100px" }}
          >
            {aiMarkupData?.aiDetectedTag ? (
              <FaroAiChip
                label={aiMarkupData.aiDetectedTag}
                size="small"
                textTransform="uppercase"
              />
            ) : (
              <FaroChipStatus status={state} />
            )}
          </Grid>
          <Grid size="grow" sx={{ minWidth: 0 }}>
            {tags && (
              <FaroChipList
                size="small"
                sx={{ flexGrow: 1 }}
                chips={tags.map((t) => (
                  <FaroChipTag
                    className={NO_TRANSLATE_CLASS}
                    key={t.id}
                    label={t.label}
                    color={t.color}
                  />
                ))}
              />
            )}
          </Grid>
          <Grid size="auto">
            <CardAttachmentIcon count={attachments.length} />
          </Grid>
          <Grid size="auto">
            <CardUserAvatar
              userDisplayInfo={mainAssignee}
              isTeam={mainAssignee?.authorityType === "UserGroupRef"}
            />
          </Grid>
          <Grid size="auto">
            <FaroTooltip title="Go to annotation">
              <FaroIconButton
                size="s"
                aria-label="go to annotation"
                onClick={onGoTo}
                margin={0}
              >
                <CompassGoToIcon />
              </FaroIconButton>
            </FaroTooltip>
          </Grid>
          <Grid size="auto">
            <CardContextMenu
              markup={markup}
              mainAssignee={mainAssignee}
              state={state}
              dueDate={dueDate}
            />
          </Grid>
        </Grid>
      </Stack>
      <FaroMenuSeparator />
    </Stack>
  );
}
